import React, { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Receipt, TrendingUp, Clock, CheckCircle, AlertTriangle, DollarSign,
  BarChart3, FileCheck
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

const COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
const STATUS_COLORS_MAP = {
  DRAFT: '#9ca3af', MATCHED: '#22c55e', VARIANCE: '#f59e0b',
  PENDING_APPROVAL: '#3b82f6', APPROVED: '#10b981', REJECTED: '#ef4444',
  PARTIALLY_PAID: '#06b6d4', PAID: '#059669', READY_FOR_PAYMENT: '#0d9488',
};

export default function InvoiceDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/api/invoices/dashboard/summary').then(r => setData(r.data)).catch(() => toast.error('Failed to load dashboard'));
  }, []);

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>;

  const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const kpis = [
    { label: 'Total Invoices', value: data.total_invoices, icon: Receipt, color: 'text-indigo-600', action: () => onNavigate?.('invoices') },
    { label: 'This Month', value: data.invoices_this_month, icon: TrendingUp, color: 'text-emerald-600' },
    { label: 'Pending Approval', value: data.pending_approval, icon: Clock, color: 'text-amber-600', action: () => onNavigate?.('invoices', { status: 'PENDING_APPROVAL' }) },
    { label: 'Ready for Payment', value: data.ready_for_payment, icon: CheckCircle, color: 'text-blue-600', action: () => onNavigate?.('invoices', { status: 'READY_FOR_PAYMENT' }) },
    { label: 'Total Gross Value', value: fmt(data.total_gross_value), icon: DollarSign, color: 'text-violet-600' },
    { label: 'Total Paid', value: fmt(data.total_paid), icon: FileCheck, color: 'text-teal-600', action: () => onNavigate?.('invoices', { status: 'PAID' }) },
    { label: 'Outstanding', value: fmt(data.outstanding), icon: AlertTriangle, color: 'text-rose-600' },
  ];

  const statusData = (data.by_status || []).map((s, i) => ({
    name: s.status, value: s.count, fill: STATUS_COLORS_MAP[s.status] || COLORS[i % COLORS.length],
  }));

  const agingData = data.aging ? [
    { name: 'Current', value: data.aging.current, fill: '#10b981' },
    { name: '30-60 days', value: data.aging.over_30, fill: '#f59e0b' },
    { name: '60-90 days', value: data.aging.over_60, fill: '#ef4444' },
    { name: '90+ days', value: data.aging.over_90, fill: '#991b1b' },
  ].filter(a => a.value > 0) : [];

  const handlePieClick = (_, idx) => {
    const entry = statusData[idx];
    if (entry?.name) onNavigate?.('invoices', { status: entry.name });
  };

  const handleSupplierClick = (entry) => {
    if (entry?.supplier) onNavigate?.('invoices', { search: entry.supplier });
  };

  const handleInvoiceClick = (inv) => {
    onNavigate?.('invoices', { search: inv.invoice_ref });
  };

  return (
    <div className="space-y-6" data-testid="invoice-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {kpis.map(k => (
          <div key={k.label} onClick={k.action} data-testid={`inv-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`border rounded-xl p-3.5 space-y-1.5 hover:shadow-sm transition-shadow ${k.action ? 'cursor-pointer hover:border-primary/40' : ''}`}>
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg bg-muted ${k.color}`}><k.icon className="h-3.5 w-3.5" /></div>
              <span className="text-[10px] font-medium text-muted-foreground leading-tight">{k.label}</span>
            </div>
            <p className="text-lg font-bold tracking-tight">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Invoice Activity (6 months)</h3>
          {(data.monthly_trend || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, n) => n === 'value' ? `$${v.toLocaleString()}` : v} />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} name="Count" dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} name="Value" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By Status */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Invoices by Status</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a segment to filter invoices</p>
          {statusData.length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}
                  label={({ name, value }) => `${name}: ${value}`} className="cursor-pointer"
                  onClick={handlePieClick}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Aging */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Invoice Aging (Unpaid)</h3>
          {agingData.length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No unpaid invoices</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={agingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Invoices" radius={[4, 4, 0, 0]}>
                  {agingData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Suppliers */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Top Suppliers by Invoice Value</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a bar to filter by supplier</p>
          {(data.top_suppliers || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={(data.top_suppliers || []).slice(0, 6)} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="supplier" tick={{ fontSize: 10 }} width={75} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Value" className="cursor-pointer"
                  onClick={(entry) => handleSupplierClick(entry)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Match Status */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Match Status Breakdown</h3>
          {(data.by_match || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <div className="space-y-3">
              {data.by_match.map((m, i) => {
                const total = data.total_invoices || 1;
                const pct = Math.round((m.count / total) * 100);
                return (
                  <div key={m.match_status} className="cursor-pointer hover:bg-muted/30 rounded-lg p-1 transition-colors"
                    onClick={() => onNavigate?.('invoices', { search: m.match_status })}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{m.match_status}</span>
                      <span className="text-muted-foreground">{m.count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="border rounded-xl p-4">
        <h3 className="text-sm font-semibold mb-3">Recent Invoices</h3>
        <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click an invoice to view it</p>
        <div className="grid grid-cols-1 gap-2 max-h-[200px] overflow-y-auto">
          {(data.recent_invoices || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No invoices</p> : (
            data.recent_invoices.map(inv => (
              <div key={inv.invoice_ref} onClick={() => handleInvoiceClick(inv)} data-testid={`inv-recent-${inv.invoice_ref}`}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 text-sm cursor-pointer border border-transparent hover:border-primary/30 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-medium text-primary">{inv.invoice_ref}</span>
                  <span className="text-muted-foreground text-xs">{inv.supplier_name}</span>
                  {inv.supplier_invoice_number && <span className="text-[10px] text-muted-foreground">#{inv.supplier_invoice_number}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-[10px]" style={{ backgroundColor: (STATUS_COLORS_MAP[inv.status] || '#999') + '20', color: STATUS_COLORS_MAP[inv.status] || '#999' }}>
                    {inv.status}
                  </Badge>
                  <span className="font-mono text-xs">${Number(inv.invoice_gross_total || 0).toLocaleString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
